import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";

import { parseTemplate, substituteVars } from "../../WabaTemplateModal/helpers";
import WabaTemplateButtons from "../../WabaTemplateModal/WabaTemplateButtons";

// Render de un mensaje de plantilla HSM (Meta) dentro del hilo. Doc 0367 (H9).
// Reconstruye header/body/footer/botones desde message.dataJson (persistido por
// SendWabaTemplateUseCase). Si no hay dataJson, cae al body plano ya renderizado.
const useStyles = makeStyles((theme) => ({
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    padding: "1px 6px",
    borderRadius: 4,
    marginBottom: 4,
  },
  headerText: { fontWeight: 700, marginBottom: 3, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  headerMedia: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.78rem",
    padding: "6px 8px",
    borderRadius: 6,
    marginBottom: 4,
  },
  body: { whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.4 },
  footer: { fontSize: "0.72rem", marginTop: 4, opacity: 0.7, whiteSpace: "pre-wrap" },
}));

const MediaLine = ({ format, url, className }) => {
  const Icon =
    format === "VIDEO" ? VideocamIcon : format === "DOCUMENT" ? InsertDriveFileIcon : ImageIcon;
  const label = format === "VIDEO" ? "Video" : format === "DOCUMENT" ? "Documento" : "Imagen";
  return (
    <div className={className}>
      <Icon fontSize="small" />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {url || label}
      </span>
    </div>
  );
};

export default function WabaTemplateThreadView({ message }) {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.mode === "dark";

  let data = null;
  try {
    data = message.dataJson ? JSON.parse(message.dataJson) : null;
  } catch {
    data = null;
  }

  const template = data?.template;
  const variables = data?.variables || {};
  const parsed = template?.components ? parseTemplate(template.components) : null;

  const badgeStyle = {
    background: isDark ? "#2a3b2e" : "#e7f3e9",
    color: isDark ? "#8fd39a" : "#2e7d32",
  };

  const Badge = (
    <div className={classes.badge} style={badgeStyle}>
      <DescriptionOutlinedIcon style={{ fontSize: 12 }} />
      Plantilla{template?.name ? ` · ${template.name}` : ""}
    </div>
  );

  // Fallback: sin components no podemos reconstruir → badge + body plano.
  if (!parsed) {
    return (
      <div>
        {Badge}
        {message.body && <div className={classes.body}>{message.body}</div>}
      </div>
    );
  }

  const { header, body, footer, buttons } = parsed;
  const bodyText = substituteVars(body?.text, variables.body || []);
  const headerText =
    header && !header.isMedia ? substituteVars(header.text, variables.header || []) : "";
  const headerMediaUrl = header?.isMedia ? variables.header?.[0] : "";

  return (
    <div>
      {Badge}

      {header?.isMedia && (
        <MediaLine
          format={header.format}
          url={headerMediaUrl}
          className={classes.headerMedia}
        />
      )}
      {header && !header.isMedia && headerText && (
        <div className={classes.headerText}>{headerText}</div>
      )}

      {bodyText && <div className={classes.body}>{bodyText}</div>}

      {footer?.text && <div className={classes.footer}>{footer.text}</div>}

      <WabaTemplateButtons buttons={buttons} />
    </div>
  );
}
